import { useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { AuthShell } from "@/components/AuthShell";
import { FormError } from "@/components/FormError";
import { authApi } from "@/api/endpoints/auth";
import { ApiError } from "@/api/client";
import { translateError } from "@/i18n/errors";

const schema = z.object({
  email: z.string().email("Enter a valid email."),
  password: z
    .string()
    .min(12, "Password must be at least 12 characters.")
    .regex(/[A-Za-z]/, "Password must include letters.")
    .regex(/\d/, "Password must include a number."),
});
type FormValues = z.infer<typeof schema>;

function passwordStrength(p: string): { label: string; score: number } {
  let score = 0;
  if (p.length >= 12) score++;
  if (p.length >= 16) score++;
  if (/[A-Z]/.test(p) && /[a-z]/.test(p)) score++;
  if (/\d/.test(p)) score++;
  if (/[^A-Za-z0-9]/.test(p)) score++;
  const label = ["Too short", "Weak", "Fair", "Good", "Strong", "Excellent"][score] ?? "Strong";
  return { label, score };
}

export function RegisterPage(): JSX.Element {
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>();
  const password = watch("password") ?? "";
  const strength = useMemo(() => passwordStrength(password), [password]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const parsed = schema.parse(values);
      return authApi.register(parsed);
    },
    onSuccess: () => setSubmitted(true),
    onError: (err) => {
      if (err instanceof ApiError) setServerError(translateError(err.code, err.message));
      else setServerError(translateError("COMMON_UNKNOWN"));
    },
  });

  if (submitted) {
    return (
      <AuthShell sideTitle="Check your email.">
        <div>
          <h1>One more step</h1>
          <p>
            We have sent a verification link if that email is available. Open it to activate
            your account.
          </p>
        </div>
        <button
          className="btn btn-primary btn-lg btn-block"
          onClick={() => navigate({ to: "/login" })}
        >
          Back to sign in
        </button>
        <p className="help" style={{ textAlign: "center" }}>
          The link expires after 24 hours. Check your spam folder if it does not arrive.
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell sideTitle="Create your workspace.">
      <div>
        <h1>Create account</h1>
        <p>Set up a free trial. You can invite teammates after sign-in.</p>
      </div>
      <form onSubmit={handleSubmit((v) => { setServerError(null); mutation.mutate(v); })} noValidate>
        <div className="field-group">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            className="field-input"
            data-testid="register-email"
            {...register("email", { required: "Email is required." })}
          />
          <FormError message={errors.email?.message} />
        </div>
        <div className="field-group">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            className="field-input"
            data-testid="register-password"
            {...register("password", { required: "Password is required." })}
          />
          <div
            className="help"
            style={{
              color:
                strength.score >= 4
                  ? "var(--success)"
                  : strength.score >= 2
                    ? "var(--text-2)"
                    : "var(--danger)",
            }}
            data-testid="password-strength"
          >
            Strength: {strength.label}
          </div>
          <FormError message={errors.password?.message} />
        </div>
        <FormError message={serverError} />
        <button
          type="submit"
          className="btn btn-primary btn-lg btn-block"
          disabled={isSubmitting || mutation.isPending}
          data-testid="register-submit"
        >
          {mutation.isPending ? "Creating account…" : "Create account"}
        </button>
        <div className="auth-foot">
          Already have an account? <Link to="/login">Sign in</Link>
        </div>
      </form>
    </AuthShell>
  );
}
